import type { InfiniteData } from "@tanstack/react-query";
import { useCallback, useEffect, useState } from "react";

import type { ChatReplyDto, ChatReplyDtoPaginationResultDto } from "@/api/types";
import { useSessionUser } from "@/hooks/Network/useSessionUser";
import { useSignalRHub, useSignalRInvocation, useSignalRSubscription } from "@/hooks/useSignalR";

interface UseChatRepliesOptions {
  chatId: string | undefined;
  repliesData: InfiniteData<ChatReplyDtoPaginationResultDto> | undefined;
  /** The SignalR event name to subscribe to for incoming replies. */
  signalREvent: string;
  disabled?: boolean;
  onReceiveNewReply?: (reply: ChatReplyDto) => void;
  onReadNewReply?: () => void;
}

export function useChatReplies({
  chatId,
  repliesData,
  signalREvent,
  disabled,
  onReceiveNewReply,
  onReadNewReply,
}: UseChatRepliesOptions): {
  chatReplies: ChatReplyDto[];
} {
  const [chatReplies, setChatReplies] = useState<ChatReplyDto[]>([]);
  const sessionUser = useSessionUser();

  const { signalRConnection } = useSignalRHub("chat-hub", {
    query: `userId=${sessionUser.id}&chatId=${chatId}`,
    disabled: disabled ?? !chatId,
  });
  const { invoke: invokeReadChatReplyEvent } = useSignalRInvocation(signalRConnection, "ChatMessageRead");

  useEffect(() => {
    // oxlint-disable-next-line react-hooks/set-state-in-effect
    setChatReplies(repliesData?.pages.flatMap((x) => x.items) ?? []);

    if (repliesData) {
      onReadNewReply?.();
    }
  }, [chatId, onReadNewReply, repliesData]);

  const handleReceiveNewChatReply = useCallback(
    async (...args: [{ chatId: string; isGroupChat: boolean; latestReply: ChatReplyDto }]) => {
      const payload = args[0];
      const latestChatReply = payload.latestReply;

      setChatReplies((prev) => [...prev, latestChatReply]);

      onReceiveNewReply?.(latestChatReply);
      await invokeReadChatReplyEvent({ chatId: payload.chatId, userId: sessionUser.id });
    },
    [invokeReadChatReplyEvent, sessionUser.id, onReceiveNewReply],
  );
  useSignalRSubscription(signalRConnection, signalREvent, handleReceiveNewChatReply);

  return { chatReplies };
}
